import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'src/redux/store';
import Api from 'src/services/ApiService';
import {
  CreatePurchaseOrderPayload,
  CreatePurchaseOrderResponse,
  CreateTaxCertificateRequest,
  CreateTaxCertificateResponse,
  GetPOReportsCSVResponse,
  GetPOReportsPayload,
  GetPOReportsResponse,
  GetProductInListResponse,
  GetProductInPayload,
  GetPurchaseOrderComputationResponse,
  GetPurchaseOrderDetailsResponse,
  GetPurchaseOrdersPayload,
  GetPurchaseOrdersResponse,
  MultiSelectPurchaseOrderPayload,
  MultiSelectPurchaseOrderResponse,
  PurchaseOrderItem
} from './types';
import { CommonAxiosResponse } from 'src/types';
import {
  createPurchaseOrderPayloadTransformer,
  dataCleanerForPurchaseOrderUpdate,
  findRemovedPurchaseOrderProductIds
} from './utils';
import { isEqual, pickBy } from 'lodash';

export const createPurchaseOrderThunk = createAsyncThunk<
  CommonAxiosResponse<CreatePurchaseOrderResponse>,
  CreatePurchaseOrderPayload,
  { state: RootState }
>('purchase-order/createPurchaseOrderThunk', async (data) => {
  const finalPayload = createPurchaseOrderPayloadTransformer(data);
  const createPoRes = await Api.post(`/po/create`, finalPayload);

  return createPoRes;
});

export const getPurchaseOrdersThunk = createAsyncThunk<
  CommonAxiosResponse<GetPurchaseOrdersResponse>,
  GetPurchaseOrdersPayload,
  { state: RootState }
>('purchase-order/getPurchaseOrdersThunk', async (data) => {
  if (data?.category === 0) {
    delete data?.category;
  }
  const new_data = { ...data, limit: 50 };
  const res = await Api.get(`/po/get`, new_data);
  return res;
});

export const getPurchaseOrderComputationThunk = createAsyncThunk<
  CommonAxiosResponse<GetPurchaseOrderComputationResponse>,
  GetPurchaseOrdersPayload,
  { state: RootState }
>('purchase-order/getPurchaseOrderComputationThunk', async (data) => {
  if (data?.category === 0) {
    delete data?.category;
  }
  const new_data = { ...data, limit: 50, action: 'computation' };
  const res = await Api.get(`/po/get`, new_data);
  return res;
});

export const getProductInListThunk = createAsyncThunk<
  CommonAxiosResponse<GetProductInListResponse>,
  GetProductInPayload
>('purchase-order/getProductInListThunk', async (data) => {
  const res = await Api.get(`/po/product_in/get`, data);
  return res;
});

export const getPurchaseOrderDetailsThunk = createAsyncThunk<
  CommonAxiosResponse<GetPurchaseOrderDetailsResponse>,
  number,
  { state: RootState }
>('purchase-order/getPurchaseOrderDetailsThunk', async (id) => {
  const res = await Api.get(`/po/get/${id}`);
  return res;
});

export const updatePurchaseOrderDRNoThunk = createAsyncThunk<
  CommonAxiosResponse<GetPurchaseOrderDetailsResponse>,
  CreatePurchaseOrderPayload,
  { state: RootState }
>('purchase-order/updatePurchaseOrderDRNoThunk', async (data) => {
  const res = await Api.patch(`/po/update/${data?.po_id}`, {
    dr_no: data?.dr_no,
    si_no: data?.si_no
  });
  return res;
});

export const cancelPurchaseOrderThunk = createAsyncThunk<
  CommonAxiosResponse<GetPurchaseOrderDetailsResponse>,
  number,
  { state: RootState }
>('purchase-order/cancelPurchaseOrderThunk', async (id) => {
  const res = await Api.patch(`/po/update/${id}`, {
    status: 'cancel'
  });
  if (!res?.success) {
    console.error('Failed to cancel the purchase order:', res?.message);
  }
  return res;
});

export const updatePurchaseOrderThunk = createAsyncThunk<
  CommonAxiosResponse<GetPurchaseOrderDetailsResponse>,
  {
    newValue: CreatePurchaseOrderPayload;
    oldValue: CreatePurchaseOrderPayload;
  },
  { state: RootState }
>('purchase-order/updatePurchaseOrderThunk', async (data) => {
  const clonedNewValue = dataCleanerForPurchaseOrderUpdate(data?.newValue);
  const clonedOrigValue: any = dataCleanerForPurchaseOrderUpdate(
    // Intended `any` type here
    data?.oldValue
  );

  // TODO: Have validation of products here also on component level

  /** ====== PRODUCT DELETION STARTS HERE ====== */
  const toBeDeletedProductsIds = findRemovedPurchaseOrderProductIds(
    clonedOrigValue?.products,
    clonedNewValue?.products
  );
  if (toBeDeletedProductsIds?.length > 0) {
    await Api.delete(`/po/products/delete/${data?.oldValue?.po_id}`, {
      po_product_ids: toBeDeletedProductsIds
    });
  }
  /** ====== PRODUCT DELETION ENDS HERE ====== */

  /** ====== PRODUCT UPDATE LOGIC STARTS HERE ====== */
  const productsWithIdAndNotForDeletion = clonedNewValue?.products?.filter(
    (x) =>
      x?.purchase_order_product_id &&
      !toBeDeletedProductsIds?.includes(x?.purchase_order_product_id)
  );
  if (
    productsWithIdAndNotForDeletion &&
    productsWithIdAndNotForDeletion?.length > 0
  ) {
    for (const product of productsWithIdAndNotForDeletion ?? []) {
      const originalProduct = clonedOrigValue?.products?.find(
        (x: PurchaseOrderItem) =>
          x?.purchase_order_product_id === product?.purchase_order_product_id
      );

      if (originalProduct) {
        const changedValues = pickBy(
          product,
          (value, key: any) => !isEqual(value, originalProduct[key])
        );

        if (changedValues && Object.keys(changedValues).length > 0) {
          const result = await Api.patch(
            `/po/products/update/${data?.oldValue?.po_id}/${product?.purchase_order_product_id}`,
            changedValues
          );

          // stop process when update products is failed
          if (!result.success) {
            return result;
          }
        }
      }
    }
  }
  /** ====== PRODUCT UPDATE LOGIC ENDS HERE ====== */

  const changedValues = pickBy(
    clonedNewValue,
    (value, key: any) => !isEqual(value, clonedOrigValue[key])
  );

  /** ====== PRODUCT ADD LOGIC STARTS HERE ====== */
  const toBeAddedProducts = clonedNewValue?.products?.filter(
    (x) => !x?.purchase_order_product_id
  );
  if (toBeAddedProducts && toBeAddedProducts?.length > 0) {
    changedValues.products = toBeAddedProducts;
  } else {
    delete changedValues.products;
  }
  /** ====== PRODUCT ADD LOGIC ENDS HERE ====== */

  const res = await Api.patch(
    `/po/update/${data?.oldValue?.po_id}`,
    changedValues
  );
  return res;
});

// export const updatePurchaseOrderProductsThunk = createAsyncThunk<
//   CommonAxiosResponse<GetPurchaseOrderDetailsResponse>,
//   CreatePurchaseOrderPayload,
//   { state: RootState }
// >('purchase-order/updatePurchaseOrderProductsThunk', async (data) => {
//   const res = await Api.patch(`/po/update/${data?.po_id}`, data);
//   return res;
// });

export const updateMultiplePurchaseOrdersThunk = createAsyncThunk<
  CommonAxiosResponse<MultiSelectPurchaseOrderResponse>,
  MultiSelectPurchaseOrderPayload,
  { state: RootState }
>('purchase-order/updateMultiplePurchaseOrdersThunk', async (data) => {
  const res = await Api.patch(`/po/status/bulk_update`, data);
  return res;
});

export const getReportsThunk = createAsyncThunk<
  CommonAxiosResponse<GetPOReportsResponse>,
  GetPOReportsPayload
>('purchase-order/getReportsThunk', async (data) => {
  const res = await Api.get(`/po/reports/get`, data);
  return res;
});

export const getReportsCsvThunk = createAsyncThunk<
  CommonAxiosResponse<GetPOReportsCSVResponse>,
  GetPOReportsPayload
>('purchase-order/getReportsCsvThunk', async (data) => {
  const res = await Api.get(`/po/reports/get`, {
    ...data,
    is_csv: 1,
    limit: 99999,
    page: 1
  });
  return res;
});

export const createTaxCertificateThunk = createAsyncThunk<
  CommonAxiosResponse<CreateTaxCertificateResponse>,
  CreateTaxCertificateRequest
>('purchase-order/createTaxCertificateThunk', async (data) => {
  const response = await Api.post('/po/tax_certificate/create', {
    ...data,
    type: '2307' // always 2307 for now
  });
  return response;
});
